export type RouteIndexPolicy = "index" | "noindex" | "redirect";

import { GALLERY_CATEGORIES, isGalleryCategoryReadyToIndex } from "./organicGrowthData";

export type RoutePolicy = {
  policy: RouteIndexPolicy;
  redirectTo?: string;
  robots?: "noindex, follow";
};

export const ROUTE_REDIRECTS: Readonly<Record<string, string>> = {
  "/neuromodulators": "/botox",
  "/hair-restoration": "/hair-growth",
  "/fractional-co2-laser": "/fractional-co2",
  "/melasma": "/pigmentation",
  "/dark-spots": "/pigmentation",
  "/privacy": "/privacy-policy",
  "/terms": "/terms-of-service",
  "/book-new": "https://medsparecord.com/book?step=0",
  "/laser-hair-removal": "/areslite-hair-removal",
  // 10/11 audit: was a 2-hop chain to /botox (Botox title, FDA, no-downtime copy). Land on the compliant noindex page instead.
  "/lp/botox-dallas": "/lp/best-med-spa-dallas",
  // Both live XERF Meta ads print "lakewoodmedspa.com/xerf" as their display
  // link. It 404'd, so anyone who read the link and typed it landed on nothing.
  "/xerf": "/xerf-skin-tightening",
  "/xerf-skin-tightening-dallas": "/xerf-skin-tightening",
  // 2026-10-11: the live Meta CO2 ad prints "lakewoodmedspa.com/co2-laser" as its display
  // link and it 404'd (an outside marketer reported it). Same fix as /xerf: every short
  // address an ad might show lands on the real service page.
  // Google XERF ads display lakewoodmedspa.com/xerf/dallas (path1/path2).
  "/xerf/dallas": "/xerf-skin-tightening",
  "/co2-laser": "/fractional-co2",
  "/co2": "/fractional-co2",
  "/rf": "/rf-microneedling",
  // 2026-10-27: /facial-balancing is now a real, indexable page (still a display link printed by live ads).
  "/facial-balancing-dallas": "/facial-balancing",
  "/blog/rf-microneedling-acne-scars-dallas": "/blog/rf-microneedling-dallas-guide",
  "/blog/semaglutide-tirzepatide-dallas-weight-loss": "/blog/medical-weight-loss-dallas-physician-guide",
  "/blog/inside-out-skin-protocol-dallas": "/blog/regenerative-aesthetics-inside-out-approach",
  "/blog/dallas-skin-summer-uv-treatment-guide": "/blog/seasonal-skin-dallas-treatment-guide",
  "/blog/hormone-optimization-aesthetics-dallas": "/blog/dallas-anti-aging-protocol-12-month-plan",
  "/blog/choosing-right-medical-spa-dallas-tx-75231-2": "/blog/choosing-right-medical-spa-dallas-tx-75231",
  "/blog/medical-weight-loss-dallas-physician-guide-1": "/blog/medical-weight-loss-dallas-physician-guide",
  // 2026-10-27 audit: these were registered as valid routes with no page behind them, so the server
  // answered 200 + index,follow with the "404 Page Not Found" screen (a true soft 404). Several are real
  // search phrases. 301 to the equivalent page. The older Search Console "Soft 404" examples
  // (thermage / coolsculpting / juvederm alternatives, stubborn-fat, prp-facial) already 404 correctly;
  // mapping them keeps whatever links still point at them. /prp has no clear equivalent, so it now 404s.
  "/home": "/",
  "/medical-weight-loss": "/weight-loss",
  "/lip-filler": "/lip-fillers",
  "/botox-dallas": "/botox",
  "/laser-hair-removal-dallas": "/areslite-hair-removal",
  "/prp-hair-rejuvenation": "/hair-growth",
  "/body-sculpting-weight-loss": "/body-sculpting",
  "/thermage-alternative-dallas": "/xerf-skin-tightening",
  "/coolsculpting-alternative-dallas": "/cryolipolysis",
  "/juvederm-alternative-dallas": "/dermal-fillers",
  "/stubborn-fat": "/stubborn-body-fat",
  "/prp-facial": "/rf-microneedling",
  "/park-cities-med-spa": "/areas-we-serve",
  "/preston-hollow-med-spa": "/areas-we-serve",
  "/uptown-dallas-med-spa": "/areas-we-serve",
  "/plano-med-spa": "/areas-we-serve",
  "/frisco-med-spa": "/areas-we-serve",
  "/lakewood-med-spa-dallas": "/areas-we-serve",
  "/richardson-med-spa": "/areas-we-serve",
  "/highland-park-med-spa": "/areas-we-serve",
  "/university-park-med-spa": "/areas-we-serve",
  "/garland-med-spa": "/areas-we-serve",
  "/downtown-dallas-med-spa": "/areas-we-serve",
  "/lake-highlands-med-spa": "/areas-we-serve",
};

export const NOINDEX_PREFIXES = ["/lp/", "/admin/"] as const;

export const NOINDEX_PATHS = new Set([
  "/ai-context",
  "/ig",
  "/booking",
  "/referral",
  "/leave-a-review",
  // Provider-partner funnel thank-you: noindex, and it fires no Lead.
  "/provider-partners/thank-you",
  // The comparison pages below are indexable — they are truthful nominative
  // comparisons against a competitor device in the SAME technology class, each
  // carrying a non-affiliation disclaimer. Keep them out of paid traffic:
  // they are organic destinations only, never a Google or Meta ad landing page.
  //
  // /hydrafacial-alternative-dallas stays noindex. Its content never names
  // HydraFacial (0 mentions) and compares AquaFacial to itself, so the mark
  // appears only in the URL with no comparative content to justify it.
  "/hydrafacial-alternative-dallas",
]);

export function normalizeRoutePath(url: string): string {
  let raw = url.split("?")[0] || "/";
  try {
    raw = decodeURI(raw);
  } catch {
    // Preserve the fail-safe path for malformed encodings.
  }
  return raw.replace(/\/+$/, "") || "/";
}

export function getRoutePolicy(url: string): RoutePolicy {
  const path = normalizeRoutePath(url);
  const redirectTo = ROUTE_REDIRECTS[path];
  if (redirectTo) return { policy: "redirect", redirectTo };
  const galleryCategory = GALLERY_CATEGORIES.find(category => path === `/gallery/${category.slug}`);
  if (galleryCategory && !isGalleryCategoryReadyToIndex(galleryCategory.slug)) {
    return { policy: "noindex", robots: "noindex, follow" };
  }
  if (NOINDEX_PREFIXES.some(prefix => path.startsWith(prefix)) || NOINDEX_PATHS.has(path)) {
    return { policy: "noindex" };
  }
  return { policy: "index" };
}

export function isNoindexRoute(url: string): boolean {
  return getRoutePolicy(url).policy === "noindex";
}
